<template>
  <div class="active-item-wrapper">

    <!-- Header and delete button -->
    <div class="active-item-header">
      <h4
        class="active-item-title is-margin-top-0"
        @click="showExtraInfo = !showExtraInfo"
      >{{ $t('ui.drag_editor.active_item.edit') }}</h4>
      <double-opt-in-delete v-if="canDelete" @delete="deleteElement" />
    </div>

    <div class="active-item-content">

      <!-- type/info badges -->
      <sensual-badge
        class="active-item-type-badge"
        color="grey"
        :text="badgeText"
        :popperOptions="popperOptions"
      />
      <sensual-badge
        class="active-item-type-badge"
        color="grey"
        :text="$t('ui.lead_mask_editor.default_field')"
        v-if="editorName == 'LeadMask' && item.isUniqueInCollection"
        :popperOptions="popperOptions"
      />

      <div
        class="element-columns"
        v-if="editorName=='LeadMask' && !['container_tab', 'title', 'paragraph', 'drawing', 'crawled_info'].includes(item.element_type)"
      >
        <!-- Element width buttons -->
        <h5>{{$t('ui.lead_mask_editor.element_width')}}</h5>
        <button
          class="btn-switch col-12"
          @click="setClassGrid('col-12', item)"
          v-bind:class="{ 'active': item.class == 'col-12' || item.class == '' }"
        ></button>
        <button
          class="btn-switch col-6"
          @click="setClassGrid('col-6', item)"
          v-bind:class="{ 'active': item.class == 'col-6'}"
        ></button>
        <button
          class="btn-switch col-4"
          @click="setClassGrid('col-4', item)"
          v-bind:class="{ 'active': item.class == 'col-4'}"
        ></button>
      </div>

      <!-- Field static content editors -->
      <field
        v-if="canEdit('content') && ['paragraph', 'confirmation'].includes(item.element_type)"
        class="editor-textarea"
        type="textarea"
        v-model="content"
        ref="content"
        :value="content"
        :label="$t('ui.drag_editor.fields.text')"
      />
      <field
        v-if="canEdit('content') && ['title', 'submit'].includes(item.element_type)"
        class="editor-textarea"
        type="text"
        v-model="content"
        ref="content"
        :value="content"
        :label="$t('ui.drag_editor.fields.text')"
      />
      <field-filler
        v-if="canEdit('content') && ['paragraph', 'confirmation'].includes(item.element_type)"
        :elements="$refs"
        v-model="content"
        element-name="content"
        :values="textAreaHtmlElements"
      />

      <!-- Field label -->
      <field
        v-if="canEdit('label') && !['paragraph', 'title'].includes(item.element_type)"
        type="text"
        v-model="label"
        :value="label"
        :label="$t('ui.drag_editor.fields.label')"
      />

      <!-- Field validation message -- is this used? -->
      <field
        v-if="canEdit('validation')"
        v-model="validation"
        type="text"
        :value="validation"
        :label="$t('ui.drag_editor.fields.validation')"
      />

      <!-- Field placeholder -->
      <field
        v-if="canEdit('placeholder')"
        v-model="placeholder"
        type="text"
        :value="placeholder"
        :label="$t('ui.drag_editor.fields.placeholder')"
      />

      <!-- Enum fields (select, radio, etc) options -->
      <drag-editor-item-options
        v-if="canEdit('allowed_values')"
        :collection="allowedValues"
        :limitEditing="limitEditingLocal"
        :element_type="item.element_type"
        :defaultValue="defaultValue"
        @input="changeAllowedValues"
        @updateDefault="changeDefaultValue"
        @delete="deleteAllowedValue"
      />
      <div v-if="hasAllowedValues() && !limitEditingLocal">
        <button class="button link" @click="addItem">
          <span class="btn-plus">+</span>
          {{ $t('ui.drag_editor.fields.add_item') }}
        </button>
      </div>

      <!-- Field default value -->
      <field
        v-if="canEdit('default_value') && canEdit('allowed_values')"
        type="hidden"
        :value="defaultValueOptions"
        :label="$t('ui.drag_editor.fields.default_value_options')"
        :disabled="true"
        :readonly="true"
      />
      <country-select
        v-else-if="item.element_type == 'country_select'"
        v-model="defaultValue"
        :label="$t('ui.drag_editor.fields.default_value')"
        :language="language">
      </country-select>
      <field
        v-else-if="canEdit('default_value') && !canEdit('allowed_values')"
        v-model="defaultValue"
        :type="item.element_type"
        :value="defaultValue"
        :label="$t('ui.drag_editor.fields.default_value')"
        :disabled="canEdit('allowed_values')"
      />

      <!-- Field custom css classes -->
      <field
        v-if="canEdit('class') && editorName == 'ExternalForm'"
        v-model="klass"
        type="text"
        :value="klass"
        :label="$t('ui.drag_editor.fields.class')"
      />

      <!-- Field required tick-box -->
      <field
        v-if="canEdit('required')"
        v-model="required"
        :defaultValue="false"
        :collection="[{value: true, name: $t('ui.drag_editor.fields.required')}]"
        type="checkbox"
        :value="required"
        name="required"
        :params="{no_i18n: true}"
        label="empty"
      />

      <!-- Field hidden tick-box -->
      <field
        v-if="canEdit('hidden')"
        v-model="hidden"
        :defaultValue="false"
        :collection="[{value: true, name: $t('ui.drag_editor.fields.hidden')}]"
        type="checkbox"
        :value="hidden"
        name="hidden"
        :params="{no_i18n: true}"
        label="empty"
      />

      <!-- Field "show as buttons" option -->
      <field
        v-if="canEdit('show_as_buttons')"
        v-model="show_as_buttons"
        :defaultValue="false"
        :collection="[{value: true, name: $t('ui.drag_editor.fields.show_as_buttons')}]"
        type="checkbox"
        :value="show_as_buttons"
        name="show_as_buttons"
        :params="{no_i18n: true}"
        label="empty"
      />

      <!-- Field "enrich tags" option -->
      <field
        v-if="canEdit('enrich_tags') && editorName == 'LeadMask' && hasPermission('is_root')"
        v-model="enrich_tags"
        :defaultValue="false"
        :collection="[{value: true, name: $t('ui.drag_editor.fields.enrich_tags')}]"
        type="checkbox"
        :value="enrich_tags"
        name="enrich_tags"
        :params="{no_i18n: true}"
        label="empty"
      />

      <!-- Field internal nane -->
      <field
        v-if="canEdit('data_type') && editorName == 'LeadMask'"
        type="select"
        :collection="dataTypes"
        v-model="data_type"
        :value="item.data_type"
        :label="$t('ui.drag_editor.fields.data_type')"
      />
      <field
        v-if="shouldShowName(editorName)"
        type="text"
        v-model="name"
        :readonly="item.isUniqueInCollection"
        :disabled="item.isUniqueInCollection"
        :value="item.name"
        :label="$t('ui.drag_editor.fields.internal_name')"
      />
    </div>

    <!-- Additional info to display for root users only -->
    <div
      v-if="showExtraInfo"
      v-can="'is_root'"
      v-for="attribute in Object.keys(item)"
      :key="attribute"
    >{{ attribute }}: {{ item[attribute] }}</div>
  </div>
</template>

<script>
import { EventBus } from "@/EventBus";
import Field from "@/components/helpers/DataInput/Field";
import DoubleOptInDelete from "@/components/helpers/Specific/DoubleOptInDelete";
import SensualBadge from "@/components/helpers/DataDisplay/SensualBadge";
import DragEditorItemOptions from "@/components/helpers/DragNDrop/DragEditorItemOptions";
import FieldFiller from "@/components/helpers/DataInput/FieldFiller";
import CountrySelect from '@/components/helpers/DataInput/CountrySelect'

export default {
  props: {
    limitEditing: {
      // Turning this to true stops the user from editing certain parameters of fields.
      // This is used in the lead sheet editor for example to limit editing to option values.
      // its set for gender to false as we dont want to get it edited
      type: Boolean,
      default: false,
    },
    editorName: {
      type: String,
      required: true,
    }
  },
  components: {
    Field,
    DragEditorItemOptions,
    DoubleOptInDelete,
    SensualBadge,
    FieldFiller,
    CountrySelect,
  },
  data() {
    return {
      dataTypes: [],
      limitEditingLocal: false,
      permitEditAttributes: [],
      permitEditAttributesSubmit: ["content", "class"],
      // Allow root to click "Edit selected element" text to display
      // data of object. For testing purposes.
      showExtraInfo: false,
      textAreaHtmlElements: [
        {
          name: "anchor_tag",
          code: "ui.drag_editor.placeholders.anchor",
          content: '<a href=""></a>'
        },
        {
          name: "bold",
          code: "ui.drag_editor.placeholders.bold",
          content: "<b></b>"
        },
        {
          name: "italic",
          code: "ui.drag_editor.placeholders.italic",
          content: "<i></i>"
        },
        {
          name: "underline",
          code: "ui.drag_editor.placeholders.underline",
          content: "<u></u>"
        }
      ]
    };
  },
  beforeUpdate() {  
    EventBus.$emit("doubleDeleteReset");
  },
  computed: {
    item() {
      let item = this.$store.getters.getDragEditorActiveItem;
      if (Object.keys(item).length === 0) {
        return null;
      }
      return item;
    },
    language() {
      return this.$store.getters.getCurrentLanguage
    },
    popperOptions() {
      if (!this.item.popperContent) {
        return null;
      }
      return {
        placement: "top",
        content: this.item.popperContent
      };
    },
    badgeText() {
      let type = this.item.element_type.replace(/_/g, " ");
      return _.startCase(`${type}`);
    },
    canDelete() {
      let undeletableAttributes = ["submit"];
      return !undeletableAttributes.includes(this.item.element_type);
    },
    // Sadly this is the least verbose way I could find to change Vuex values
    // without getting errors in the console
    name: {
      get() {
        return this.item.name;
      },
      set(value) {
        this.set("name", value);
      }
    },
    placeholder: {
      get() {
        return this.item.placeholder;
      },
      set(value) {
        this.set("placeholder", value);
      }
    },
    klass: {
      get() {
        return this.item["class"];
      },
      set(value) {
        this.set("class", value);
      }
    },
    required: {
      get() {
        return this.item.required;
      },
      set(value) {
        this.set("required", value);
      }
    },
    hidden: {
      get() {
        return this.item.hidden;
      },
      set(value) {
        this.set("hidden", value);
      }
    },
    show_as_buttons: {
      get() {
        return this.item.show_as_buttons;
      },
      set(value) {
        this.set("show_as_buttons", value);
      }
    },
    data_type: {
      get() {
        return this.item.data_type;
      },
      set(value) {
        this.set("data_type", value);
      }
    },
    enrich_tags: {
      get() {
        return this.item.enrich_tags;
      },
      set(value) {
        this.set("enrich_tags", value);
      }
    },
    label: {
      get() {
        return this.item.label;
      },
      set(value) {
        this.set("label", value);
      }
    },
    allowedValues: {
      get() {
        return this.item.allowed_values;
      },
      set(value) {
        this.set("allowed_values", value);
      }
    },
    validation: {
      get() {
        return this.item.validation.message;
      },
      set(value) {
        let validation = this.item.validation;
        validation.message = value;
        this.set("validation", validation);
      }
    },
    defaultValue: {
      get() {
        return this.item.default_value;
      },
      set(value) {
        this.set("default_value", value);
      }
    },
    defaultValueOptions() {
      if (this.item.allowed_values) {
        let defaultValue = this.item.default_value;
        let allowedValue = this.item.allowed_values.find(allowed => {
          return allowed.value === defaultValue;
        });
        if (allowedValue) {
          return allowedValue.name;
        } else {
          return this.item.default_value;
        }
      }
      return null;
    },
    content: {
      get() {
        return this.item.content;
      },
      set(value) {
        this.set("content", value);
      }
    }
  },
  beforeMount() {
    this.limitEditingLocal = this.limitEditing

    this.dataTypes = []
    let dataTypesObject = this.$t('ui.drag_editor.fields.data_types')
    Object.keys(dataTypesObject).forEach((value) => {
      this.dataTypes.push({
        name: dataTypesObject[value],
        value: value
      })
    })
  },
  mounted() {
    this.setPermitEditAttributes();
  },
  methods: {
    setPermitEditAttributes() {
      this.limitEditingLocal = this.limitEditing
      if (this.item) {
        if (this.item.element_type === "submit") {
          this.setupSubmit();
        } else if (this.item.element_type === "container_tab") {
          this.permitEditAttributes = ["label"];
        } else {
          this.permitEditAttributes = [...Object.keys(this.item)];
        }

        // limit editing for gender as we dont want to manipulate the values
        if (this.item.name == 'gender') {
          this.limitEditingLocal = true
        }
      }
    },
    setupSubmit() {
      // We have some certain rules for the submit button. Fx. don't allowed setting hidden/required
      this.permitEditAttributes = this.permitEditAttributesSubmit;
    },
    changeDefaultValue(value) {
      this.defaultValue = value;
    },
    deleteAllowedValue(item) {
      let clonedAllowedValues = _.cloneDeep(this.allowedValues);
      let foundValue = clonedAllowedValues.find(
        val => val.value === item.value
      );
      let idx = clonedAllowedValues.indexOf(foundValue);
      clonedAllowedValues.splice(idx, 1);
      this.allowedValues = clonedAllowedValues;
    },
    changeAllowedValues(value) {
      this.allowedValues = value;
    },
    deleteElement() {
      this.$emit("delete", this.item);
      this.$store.commit("SET_DRAG_EDITOR_ACTIVE", {});
    },
    set(attribute, value) {
      this.$store.commit("UPDATE_DRAG_EDITOR_ACTIVE", {
        attribute: attribute,
        value: value
      });
    },
    hasAllowedValues() {
      // check if this item has allowed values
      if (this.allowedValues) {
        return true;
      } else {
        return false;
      }
    },
    canEdit(key) {
      // Shorthand to check if key is allowed to be edited
      return this.permitEditAttributes.includes(key);
    },
    addItem() {
      let allowed_values = _.cloneDeep(this.allowedValues);
      allowed_values.push({
        name: "Name",
        value: `value_${allowed_values.length + 1}`
      });
      this.changeAllowedValues(allowed_values);
    },
    setClassGrid(col, item) {
      this.set("class", col);
    },
    shouldShowName(editorName) {
      if (editorName == "ExternalForm") {
        return false;
      }
      return !["paragraph", "title", "container_tab", "submit"].includes(
        this.item.element_type
      );
    }
  },
  watch: {
    item() {
      // Reset allowed attributes
      this.setPermitEditAttributes();
    }
  }
};
</script>
<style lang="sass" scoped>
.active-item-wrapper
  .element-columns
    margin-bottom: 30px
    h5
      margin-bottom: 10px !important
      color: $ink !important
      margin-top: 10px !important
    .btn-switch
      background-repeat: no-repeat
      width: 28px
      height: 28px
      border: none
      cursor: pointer
      margin-right: 15px
      transition: all .15s ease-out
    .col-12
      background-image: url("/static/col-12.svg")
      &.active
        background-image: url("/static/col-12-active.svg")
      &:hover
        opacity: 0.8
    .col-6
      background-image: url("/static/col-6.svg")
      &.active
        background-image: url("/static/col-6-active.svg")
      &:hover
        opacity: 0.8
    .col-4
      background-image: url("/static/col-4.svg")
      &.active
        background-image: url("/static/col-4-active.svg")
      &:hover
        opacity: 0.8

  .active-item-title
    margin-bottom: 5px

  .active-item-type-badge
    margin-bottom: 15px

  .active-item-header
    display: flex
    justify-content: space-between
    align-items: center

  .delete-icon
    cursor: pointer

  .link
    background: white
    border: none
    color: $sensual
    cursor: pointer
    font-size: 16px
    font-weight: $font-medium
    height: 40px
    margin-bottom: 40px
    &:hover
      color: $sensual-dark
    .btn-plus
      border: 2px dashed $sensual-light
      background: white
      border-radius: 5px
      padding: 5px 12px
      margin-right: 10px
      color: $sensual
      cursor: pointer
      font-weight: $font-medium
      transition: all .15s ease-out
      &:hover
        border: 2px dashed $sensual !important
</style>



// WEBPACK FOOTER //
// DragItemEdit.vue?afc348f4